import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Checkbox, FormControlLabel,
  Alert, CircularProgress, Tab, Tabs,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useMutation, useQueryClient } from 'react-query';
import { acceptTos } from '../../api/developerApi';
import wordmark from '../../assets/vectros-wordmark-dark.svg';

import { TOS_CONTENT, PRIVACY_CONTENT } from '../../assets/legalContent';

export default function LegalAcceptancePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');

  const { mutate, isLoading } = useMutation(acceptTos, {
    onSuccess: () => {
      queryClient.invalidateQueries('legal-status');
      navigate('/dashboard', { replace: true });
    },
    onError: (e) => setError(e?.response?.data?.message || 'Acceptance failed. Please try again.'),
  });

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#F9FAFB',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      py: 6, px: 2,
    }}>
      <img src={wordmark} alt="Vectros" style={{ height: 28, marginBottom: 32 }} />

      <Box sx={{ width: '100%', maxWidth: 720 }}>
        <Typography variant="h5" fontWeight={700} mb={0.5} textAlign="center">
          Review and accept our terms
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
          Please read and agree to the following before continuing.
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #E4E4E7' }}>
          <Tab label="Terms of Service" />
          <Tab label="Privacy Policy" />
        </Tabs>

        <Box sx={{
          height: 400, overflowY: 'auto',
          border: '1px solid #E4E4E7', borderTop: 'none',
          borderRadius: '0 0 8px 8px', bgcolor: '#fff', p: 3,
          '& h1,h2,h3': { fontWeight: 700, mt: 2, mb: 1 },
          '& p': { mb: 1.5, lineHeight: 1.7, fontSize: 14 },
          '& ul,ol': { pl: 3, mb: 1.5 },
          '& li': { fontSize: 14, mb: 0.5 },
        }}>
          <ReactMarkdown>{tab === 0 ? TOS_CONTENT : PRIVACY_CONTENT}</ReactMarkdown>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
            label={
              <Typography variant="body2">
                I have read and agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>
              </Typography>
            }
          />
          <Button
            variant="contained"
            disabled={!agreed || isLoading}
            onClick={() => mutate()}
            sx={{ alignSelf: 'flex-start', bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, fontWeight: 600 }}
          >
            {isLoading
              ? <CircularProgress size={18} sx={{ color: '#fff' }} />
              : 'Continue to Vectros →'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
